import { apiMaker } from '@/services-ts/apiConfig'
import type { ITokenManager, RefreshOutcome } from '@/types/Utils/ITokenManager'
import axios from 'axios'
import { jwtDecode } from 'jwt-decode'
import {
  GetRefreshToken,
  GetToken,
  GetTokenValidTo,
  HasSessionHint,
  IsSessionEnded,
  MarkSessionEnded,
  RemoveToken,
  SetToken,
  SetTokens,
} from './Cookie'

export class TokenManager implements ITokenManager {
  private static instance: TokenManager
  private token: string | null = null
  private refreshToken: string | null = null

  private constructor() {}

  static GetInstance(): TokenManager {
    TokenManager.instance ??= new TokenManager()
    return TokenManager.instance
  }

  Initialize(): void {
    const token = GetToken()
    const refreshToken = GetRefreshToken()
    if (token) {
      this.token = token
      this.refreshToken = refreshToken
    }
  }

  GetToken(): string | null {
    // Always refresh token from cookie to ensure we have the latest token
    const cookieToken = GetToken()
    if (cookieToken && cookieToken !== this.token) {
      this.token = cookieToken
    } else if (!cookieToken) {
      this.token = null
    }

    return this.token
  }

  async RefreshAccessToken(): Promise<RefreshOutcome> {
    if (IsSessionEnded()) return { status: 'rejected' }
    const refreshToken = GetRefreshToken()

    try {
      const baseURL = apiMaker('apiUser')
      console.log('--- REFRESHING TOKEN ---', `${baseURL}Login/RefreshToken`)

      const response = await axios.post(
        `${baseURL}Login/RefreshToken`,
        refreshToken ? { RefreshToken: refreshToken, Platform: 'Admin' } : { Platform: 'Admin' },
        { withCredentials: true },
      )

      if (
        response.data?.status &&
        typeof response.data?.data?.token === 'string' &&
        response.data.data.token
      ) {
        const {
          token: newToken,
          refreshToken: newRefreshToken,
          tokenValidTo,
          refreshTokenValidTo,
        } = response.data.data

        this.token = newToken as string
        if (newRefreshToken) {
          this.refreshToken = newRefreshToken as string
          SetTokens(this.token, this.refreshToken, tokenValidTo, refreshTokenValidTo)
        } else {
          SetToken(this.token, tokenValidTo)
        }
        return { status: 'ok', token: this.token }
      }

      MarkSessionEnded()
      this.ClearTokens()
      return { status: 'rejected' }
    } catch (error) {
      const httpStatus = axios.isAxiosError(error) ? error.response?.status : undefined
      const isServerRejection = httpStatus === 400 || httpStatus === 401

      if (!isServerRejection) {
        console.warn('Refresh geçici olarak başarısız; tokenlar korunuyor.', {
          httpStatus,
          error,
        })
        return { status: 'transient' }
      }

      MarkSessionEnded()
      this.ClearTokens()
      return { status: 'rejected' }
    }
  }

  IsTokenExpired(): boolean {
    const token = this.GetToken()
    const refreshToken = GetRefreshToken()

    if (!token && (refreshToken || HasSessionHint())) return true
    if (!token) return false

    const bufferTime = 60 * 1000
    const tokenValidTo = GetTokenValidTo()

    if (tokenValidTo) {
      const validToDate = new Date(tokenValidTo)
      if (!Number.isNaN(validToDate.getTime())) {
        return validToDate.getTime() < Date.now() + bufferTime
      }
    }

    // TokenValidToAUID cookie henüz yoksa (eski oturum vb.) token içindeki exp'e düş
    try {
      const decoded = jwtDecode(token)
      if (!decoded.exp) return true
      return decoded.exp < Date.now() / 1000 + bufferTime / 1000
    } catch (e) {
      console.warn('Failed to decode token', e)
      return true
    }
  }

  ClearTokens(): void {
    this.token = null
    this.refreshToken = null
    RemoveToken()
  }
}
